import axios from '../axios';
import {
  AnalyticsOverviewResponse,
  AnalyticsReportResponse,
  AnalyticsSection,
  AdminAnalyticsFilters,
  UserActivityQueryParams,
  UserActivityResponse,
} from '../types/adminAnalytics.ts';

const getAuthHeaders = () => {
  const authToken = localStorage.getItem('access-Token-Edify');
  return { headers: { Authorization: `Bearer ${authToken}` } };
};

export function buildAnalyticsQuery(
  filters: AdminAnalyticsFilters,
  options?: { sections?: AnalyticsSection[]; format?: 'xlsx' | 'csv' }
): URLSearchParams {
  const q = new URLSearchParams();
  q.set('from', filters.from);
  q.set('to', filters.to);
  if (filters.useExplicitCompare && filters.compareFrom && filters.compareTo) {
    q.set('compareFrom', filters.compareFrom);
    q.set('compareTo', filters.compareTo);
  }
  if (filters.includeDeleted) q.set('includeDeleted', 'true');
  if (filters.usersValidatedOnly) q.set('usersValidatedOnly', 'true');
  const sections = options?.sections;
  if (sections?.length) {
    q.set('sections', sections.join(','));
  }
  if (options?.format) q.set('format', options.format);
  return q;
}

function buildUserActivityQuery(params: UserActivityQueryParams): URLSearchParams {
  const q = new URLSearchParams();
  q.set('windowDays', String(params.windowDays));
  if (params.scope) q.set('scope', params.scope);
  if (params.asOf) q.set('asOf', params.asOf);
  if (params.includeDeleted) q.set('includeDeleted', 'true');
  if (params.usersValidatedOnly) q.set('usersValidatedOnly', 'true');
  if (params.page != null) q.set('page', String(params.page));
  if (params.take != null) q.set('take', String(params.take));
  return q;
}

function parseContentDispositionFilename(
  disposition: string,
  fallback: string
): string {
  const match = disposition.match(/filename="([^"]+)"/);
  return match?.[1] ?? fallback;
}

export const adminAnalyticsApi = {
  fetchOverview: async (filters: AdminAnalyticsFilters): Promise<AnalyticsOverviewResponse> => {
    const query = buildAnalyticsQuery(filters);
    const response = await axios.get<AnalyticsOverviewResponse>(
      `/admin/analytics/overview?${query}`,
      getAuthHeaders()
    );
    return response.data;
  },

  fetchSection: async (
    section: AnalyticsSection,
    filters: AdminAnalyticsFilters
  ): Promise<AnalyticsOverviewResponse> => {
    const query = buildAnalyticsQuery(filters, { sections: [section] });
    const response = await axios.get<AnalyticsOverviewResponse>(
      `/admin/analytics/${section}?${query}`,
      getAuthHeaders()
    );
    return response.data;
  },

  fetchReport: async (filters: AdminAnalyticsFilters): Promise<AnalyticsReportResponse> => {
    const query = buildAnalyticsQuery(filters);
    const response = await axios.get<AnalyticsReportResponse>(
      `/admin/analytics/report?${query}`,
      getAuthHeaders()
    );
    return response.data;
  },

  downloadExport: async (
    filters: AdminAnalyticsFilters,
    format: 'xlsx' | 'csv' = 'xlsx'
  ): Promise<{ blob: Blob; filename: string }> => {
    const query = buildAnalyticsQuery(filters, { format });
    const response = await axios.get(`/admin/analytics/export?${query}`, {
      ...getAuthHeaders(),
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] ?? '';
    const filename = parseContentDispositionFilename(disposition, `edify-analytics.${format}`);
    return { blob: response.data, filename };
  },

  fetchUserActivity: async (params: UserActivityQueryParams): Promise<UserActivityResponse> => {
    const query = buildUserActivityQuery(params);
    const response = await axios.get<UserActivityResponse>(
      `/admin/analytics/users/activity?${query}`,
      getAuthHeaders()
    );
    return response.data;
  },

  downloadUserActivityExport: async (
    params: UserActivityQueryParams
  ): Promise<{ blob: Blob; filename: string }> => {
    const query = buildUserActivityQuery(params);
    const response = await axios.get(`/admin/analytics/users/activity/export?${query}`, {
      ...getAuthHeaders(),
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] ?? '';
    const filename = parseContentDispositionFilename(
      disposition,
      `edify-users-activity-${params.windowDays}d.csv`
    );
    return { blob: response.data, filename };
  },
};

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
